import { useCallback } from "react";

import { AccountViewer } from "components/form/graphql/types";
import useTypedContext from "hooks/useTypedContext";
import useSelfServiceFlags from "hooks/useSelfServiceFlags";
import { Action, Stack, StackState } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackWriteAccess from "./useHasStackWriteAccess";

const CONFIRM_ACCESS_TYPES = [Action.RunConfirm];

const hasConfirmAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(CONFIRM_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that determines if a user can confirm a stack run
 * Stacks created from a template deployment (blueprintDeployment) cannot have runs confirmed.
 * @returns A function that takes a stack and returns an access result indicating whether the user can confirm the stack run
 */
const useCanConfirmStackRun = (): ((stack: Stack) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const { enableSelfServiceV2 } = useSelfServiceFlags();
  const hasStackWriteAccess = useHasStackWriteAccess();

  return useCallback(
    (stack: Stack) => {
      const blueprintCheck = checkTemplateDeploymentAccess(enableSelfServiceV2, stack);
      if (blueprintCheck) {
        return blueprintCheck;
      }

      if (stack.state !== StackState.Unconfirmed) {
        return { result: false };
      }

      if (viewer.admin) {
        return { result: true };
      }

      if (hasStackWriteAccess(stack).result) {
        return { result: true };
      }

      // TODO: This is a temporary solution until we have proper types for query results
      if (hasConfirmAccess(viewer, stack.spaceDetails?.id || stack.space)) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [viewer, hasStackWriteAccess, enableSelfServiceV2]
  );
};

export default useCanConfirmStackRun;
